import { useLiveQuery } from "@tanstack/react-db";
import { communityAccess } from "@softmax/web-features/community";
import type { WikiViewProps } from "@softmax/web-ux/wiki";
import type {
  WikiPagePublic,
  WikiPageListPublic,
} from "@softmax/observatory-client/types";
import {
  wikiIndexCollection,
  wikiPermissionsCollection,
  wikiRevisionsCollection,
  type WikiIdentity,
} from "./wiki.collections";
import { wikiPageCollection } from "./wiki-page.collection";
import { useWikiRefresh } from "./use-wiki-refresh";
import { wikiReadStates } from "./wiki-read-state";

export function useWikiReads({
  wikiSlug,
  pageSlug,
  elevatedPrivileges,
  initialPage,
  initialIndex,
  initialMissing,
  initialDataUpdatedAt,
  mode,
  historyOpened,
}: WikiIdentity & {
  readonly initialPage?: WikiPagePublic | null | undefined;
  readonly initialIndex?: WikiPageListPublic | undefined;
  readonly initialMissing?: boolean | undefined;
  readonly initialDataUpdatedAt?: number | undefined;
  readonly mode: WikiViewProps["mode"];
  readonly historyOpened: boolean;
}) {
  const scope = { wikiSlug, pageSlug, elevatedPrivileges };
  const permissionsDescriptor = wikiPermissionsCollection(scope);
  const permissions = useLiveQuery((q) =>
    q.from({ permissions: permissionsDescriptor }).findOne(),
  );
  const indexDescriptor = wikiIndexCollection({
    ...scope,
    initialIndex: initialIndex,
    initialDataUpdatedAt: initialDataUpdatedAt,
  });
  const indexQuery = useLiveQuery({
    query: (q) =>
      permissions.data
        ? q
            .from({ page: indexDescriptor })
            .orderBy(({ page }) => page.slug, "asc")
        : undefined,
  });
  const pageDescriptor = wikiPageCollection({
    ...scope,
    initialPage: initialPage,
    initialMissing: initialMissing,
    initialDataUpdatedAt: initialDataUpdatedAt,
  });
  const pageQuery = useLiveQuery({
    query: (q) =>
      permissions.data ? q.from({ page: pageDescriptor }).findOne() : undefined,
  });
  const page = pageQuery.data ?? null;
  const missing = pageQuery.isEnabled && pageQuery.isReady && page === null;
  const revisionsQuery = useLiveQuery({
    query: (q) =>
      permissions.data && historyOpened && page
        ? q
            .from({
              revision: wikiRevisionsCollection({ ...scope, pageId: page.id }),
            })
            .orderBy(({ revision }) => revision.created_at, "desc")
            .orderBy(({ revision }) => revision.id, "desc")
        : undefined,
  });
  const { accessLost, error: accessError } = communityAccess([
    permissions.health,
    pageQuery.health,
    revisionsQuery.health,
  ]);
  const indexAccess = communityAccess([indexQuery.health]);
  const { refreshing, refresh } = useWikiRefresh({
    scope,
    mode,
    pageId: page?.id,
    permissions: permissions.collection,
    page: pageQuery.collection,
    index: indexQuery.collection,
    revisions: revisionsQuery.collection,
  });

  return {
    page,
    accessLost,
    refresh,
    historyFailed: revisionsQuery.isLoadingError,
    states: wikiReadStates({
      permissions,
      pageQuery,
      indexQuery,
      revisionsQuery,
      missing,
      accessLost,
      accessError,
      indexAccessLost: indexAccess.accessLost,
      refreshing,
    }),
  };
}
